import React, { useContext, useMemo } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { Box, Divider, Text } from '@vividseats/vivid-ui-kit'

import FeaturedProductionsContext from '@/components/pages/performer/context'
import LocationFilter from '@/components/shared/filters/location-filter'
import { useShouldOpenProductionInNewTab } from '@/components/shared/production-listings/hooks/should-open-in-new-tab/hook'
import { CATEGORY_IDS } from '@/constants'
import performerContext from '@/context/performer'
import { AnalyticsSubIds } from '@/context/utils/analytics'
import { useAnalytics, useTrackViewProductionListWithAnalyticsId } from '@/hooks/use-analytics'
import { useListingSkeletonBone } from '@/hooks/use-skeleton'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { useMlbPerformerOpeningDayDesktopFeature } from '@/optimizely/features/mlb-performer-opening-day-feature/hooks/use-mlb-performer-opening-day-desktop-feature'
import { useMlbPerformerOpeningDayMobileFeature } from '@/optimizely/features/mlb-performer-opening-day-feature/hooks/use-mlb-performer-opening-day-mobile-feature'
import {
    filterMlbNearbyProductions,
    updateHomeOpenerProduction,
} from '@/optimizely/features/mlb-performer-opening-day-feature/utils'
import { MAX_PRODUCTIONS as NCAA_BASKETBALL_MAX_PRODUCTIONS } from '@/optimizely/features/ncaa-basketball-performer-near-you-feature/constants'
import { useNcaaBasketballPerformerNearYouDesktopFeature } from '@/optimizely/features/ncaa-basketball-performer-near-you-feature/hooks/use-ncaa-basketball-performer-near-you-desktop-feature'
import { useNcaaBasketballPerformerNearYouMobileFeature } from '@/optimizely/features/ncaa-basketball-performer-near-you-feature/hooks/use-ncaa-basketball-performer-near-you-mobile-feature'
import { useSellingFastDealsAvailableConcertPerformerFeature } from '@/optimizely/features/selling-fast-deals-available-concert-performer-feature/hooks'
import { useSellingFastDealsAvailableSportsHomePerformerFeature } from '@/optimizely/features/selling-fast-deals-available-sports-home-performer-feature/hooks'
import { useSellingFastDealsAvailableSportsPerformerFeature } from '@/optimizely/features/selling-fast-deals-available-sports-performer-feature/hooks'
import { useSellingFastDealsAvailableTheatrePerformerFeature } from '@/optimizely/features/selling-fast-deals-available-theatre-performer-feature/hooks'
import type { Production } from '@/types/app-types'
import { PageType } from '@/types/app-types'
import { trackSelectProduction } from '@/utils/analytics'
import { trackEvent } from '@/utils/analytics/ga360'

import MobileProductionListingRow from '../mobile-production-listing-row'
import ProductionNearMeListing from '../production-near-me-listing'

type Props = WithOptimizelyProps & { categoryId?: number; semCity?: string }

const NearbyProductions: React.FC<Props> = ({ optimizely, categoryId, semCity }) => {
    const { nearbyProductions, nearbyBadgedProductions, showCountLabel, ticketsLeftLabels, isLoading } =
        useContext(FeaturedProductionsContext)
    const { selectors: performerSelectors } = useContext(performerContext)

    const ListingSkeleton = useListingSkeletonBone({
        isLoading: Boolean(isLoading),
    })

    const { analyticsId, analyticsPageType } = useAnalytics(AnalyticsSubIds.NearbyProductions)

    const analyticsProductions: Production[] = useMemo(
        () => nearbyProductions?.map(({ analytics }) => analytics.production) ?? [],
        [nearbyProductions],
    )
    useTrackViewProductionListWithAnalyticsId(
        analyticsId,
        !isLoading && !!analyticsProductions.length,
        analyticsProductions,
    )

    const [isNcaaBasketballPerformerNearYouDesktopFeatureEnabled] = useNcaaBasketballPerformerNearYouDesktopFeature()
    const [isNcaaBasketballPerformerNearYouMobileFeatureEnabled] = useNcaaBasketballPerformerNearYouMobileFeature()
    const isNcaaBasketballPerformerNearYouFeatureEnabled =
        isNcaaBasketballPerformerNearYouDesktopFeatureEnabled || isNcaaBasketballPerformerNearYouMobileFeatureEnabled

    const [isSellingFastDealsAvailableConcertPerformerFeatureEnabled] =
        useSellingFastDealsAvailableConcertPerformerFeature()
    const [isSellingFastDealsAvailableTheatrePerformerFeatureEnabled] =
        useSellingFastDealsAvailableTheatrePerformerFeature()
    const [isSellingFastDealsAvailableSportsPerformerFeatureEnabled] =
        useSellingFastDealsAvailableSportsPerformerFeature()
    const [isSellingFastDealsAvailableSportsHomePerformerFeatureEnabled] =
        useSellingFastDealsAvailableSportsHomePerformerFeature()
    const isUsingSellingFastDealsAvailableBadging =
        isSellingFastDealsAvailableConcertPerformerFeatureEnabled ||
        isSellingFastDealsAvailableTheatrePerformerFeatureEnabled ||
        isSellingFastDealsAvailableSportsPerformerFeatureEnabled ||
        isSellingFastDealsAvailableSportsHomePerformerFeatureEnabled

    const [isMlbPerformerOpeningDayMobileFeatureEnabled] = useMlbPerformerOpeningDayMobileFeature()
    const [isMlbPerformerOpeningDayDesktopFeatureEnabled] = useMlbPerformerOpeningDayDesktopFeature()
    const isMlbPerformerOpeningDayFeatureEnabled =
        isMlbPerformerOpeningDayMobileFeatureEnabled || isMlbPerformerOpeningDayDesktopFeatureEnabled

    const shouldOpenInNewTab = useShouldOpenProductionInNewTab()

    const productions = useMemo(
        () =>
            isMlbPerformerOpeningDayFeatureEnabled
                ? filterMlbNearbyProductions(nearbyProductions)
                : isNcaaBasketballPerformerNearYouFeatureEnabled
                ? nearbyProductions?.slice(0, NCAA_BASKETBALL_MAX_PRODUCTIONS)
                : isUsingSellingFastDealsAvailableBadging
                ? nearbyBadgedProductions
                : nearbyProductions,
        [
            isMlbPerformerOpeningDayFeatureEnabled,
            isNcaaBasketballPerformerNearYouFeatureEnabled,
            isUsingSellingFastDealsAvailableBadging,
            nearbyProductions,
            nearbyBadgedProductions,
        ],
    )

    const label = isNcaaBasketballPerformerNearYouFeatureEnabled
        ? `${productions?.length}${showCountLabel?.substring(showCountLabel.indexOf(' '))}`
        : isMlbPerformerOpeningDayFeatureEnabled
        ? 'Upcoming Games Near '
        : showCountLabel

    const handleClick = (id: number, index: number, production: Production, isLowPricesDealsAvailable?: boolean) => {
        optimizely?.track(OPTIMIZELY_EVENT.productionLinkClick)
        trackEvent(
            'Production Selection',
            `${analyticsPageType} Page`,
            `Near You${index === 0 ? ' - Featured Production' : ''}`, // The first item is the featured production
            id,
            false,
        )

        trackSelectProduction({
            item_list_name: analyticsId,
            index,
            production,
        })

        /* istanbul ignore next */
        if (isLowPricesDealsAvailable) {
            optimizely?.track(OPTIMIZELY_EVENT.lowPricesDealsAvailableProductionClick)
        }
    }

    // the skeleton will display until the data finish loading
    // if no data, it will return an empty fragment
    if (!nearbyProductions?.length && !showCountLabel) return <ListingSkeleton />

    /* istanbul ignore next */
    if (isMlbPerformerOpeningDayFeatureEnabled && !isLoading && productions?.length) {
        updateHomeOpenerProduction(performerSelectors[0].name, productions)
    }

    return (
        <Box data-testid="nearby-productions-container" mobileMarginBottom={3} tabletMarginBottom={0}>
            <Box
                mobilePaddingLeft={3}
                mobilePaddingRight={3}
                mobileMarginTop={0}
                tabletMarginTop={3}
                tabletPaddingLeft={5}
                tabletPaddingRight={5}
            >
                {showCountLabel && (
                    <Box
                        data-testid="nearby-productions-location-label"
                        marginBottom={3}
                        mobilePaddingTop={0}
                        tabletPaddingTop={3}
                    >
                        <Text as="h2" size="xl" weight="bold">
                            {label + ' '}
                            {categoryId !== CATEGORY_IDS.SPORTS ? (
                                <LocationFilter overrideCity={semCity} pageType={PageType.Performer} showCityState />
                            ) : (
                                semCity ?? 'You'
                            )}
                        </Text>
                    </Box>
                )}
                <div data-testid="nearby-productions-list">
                    {productions?.map((production, index) => {
                        const {
                            id: productionId,
                            href,
                            venueName,
                            location,
                            date,
                            name,
                            time,
                            day,
                            analytics,
                            isSellingFast,
                            isLowPricesDealsAvailable,
                            isHomeOpener,
                        } = production

                        const productionNearMeRowProps = {
                            title: name || '',
                            label: venueName || '',
                            sublabel: location || '',
                            date,
                            time,
                            day,
                            pillLabel: ticketsLeftLabels && ticketsLeftLabels[productionId],
                            target: shouldOpenInNewTab ? '_blank' : undefined,
                        }

                        return (
                            <Box
                                mobilePaddingTop={3}
                                mobilePaddingBottom={3}
                                tabletPaddingTop={5}
                                tabletPaddingBottom={5}
                                key={index}
                            >
                                <Box mobileDisplay="none" tabletDisplay="block">
                                    <ProductionNearMeListing
                                        isSellingFast={isSellingFast}
                                        isLowPricesDealsAvailable={isLowPricesDealsAvailable}
                                        isHomeOpener={isHomeOpener}
                                        productionId={productionId}
                                        productionNearMeRowProps={{
                                            ...productionNearMeRowProps,
                                            href,
                                            onClickCapture: () =>
                                                handleClick(
                                                    productionId,
                                                    index,
                                                    analytics?.production,
                                                    isLowPricesDealsAvailable,
                                                ),
                                        }}
                                    />
                                </Box>
                                <Box tabletDisplay="none">
                                    <MobileProductionListingRow
                                        date={date}
                                        day={day}
                                        href={href}
                                        isSellingFast={isSellingFast}
                                        isLowPricesDealsAvailable={isLowPricesDealsAvailable}
                                        isHomeOpener={isHomeOpener}
                                        location={location}
                                        time={time}
                                        title={name}
                                        venueName={venueName}
                                        onClickCapture={() =>
                                            handleClick(
                                                productionId,
                                                index,
                                                analytics?.production,
                                                isLowPricesDealsAvailable,
                                            )
                                        }
                                    />
                                </Box>
                            </Box>
                        )
                    })}
                </div>
            </Box>
            <Box mobileDisplay="none" tabletDisplay="block">
                <Divider data-testid="divider" />
            </Box>
        </Box>
    )
}

export default withOptimizely(NearbyProductions)
